// Global Instructions Rule Applied!
//
// The company's short description for reports (2026-11-03): one or two
// sentences on what the company does, printed on the Investor Performance
// Report's company cards, beside a new investment in the Quarterly Fund
// Report and on the SPV report's Company Profile. The Description field
// above stays the CRM's own text.
//
// An agent writes the missing ones (Configuration → Follower Agents →
// Company short descriptions). Its text prints at once but carries the
// `blue` Agent-written + `gold` Not signed off Tags until a person signs it
// off here (the one closing action, primary, confirmed through
// Modal.useModal — the highlight set grammar) or edits it, which makes it
// the person's own. Reports warn in their preview while it is unreviewed.
import React, { useCallback, useMemo } from 'react'
import { Button, Modal, Spin, Tag, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { FieldConfig, FieldTypeEnum } from '../../../../../core/components/InputManager/types.ts'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { API_ROUTES } from '../../../../../core/utils/endpoints.ts'
import { useCompanyShortDescription } from '../../../hooks/useCompanyShortDescription.ts'
import { ShortDescriptionStatus } from '../../../../../state_manager/data_models/company_short_description.ts'

const { Text } = Typography

const FIELD_KEY = 'accounts__short_description'
// InputManager builds `${backend}/${patchEndpoint}`: the route path without
// its leading slash.
const PATCH_ENDPOINT = API_ROUTES.COMPANIES.SHORT_DESCRIPTION_PATCH.path.replace(/^\//, '')
const DATE_FORMAT = 'MM/DD/YYYY'

export function ShortDescriptionSection({ accountId }: { accountId: string }) {
  const { record, loading, error, refetch, signOff, signingOff } = useCompanyShortDescription(accountId)
  const [modal, modalContext] = Modal.useModal()

  const maxChars = record?.prog__max_chars ?? 240
  const text = record?.accounts__short_description ?? ''
  const status = record?.prog__status ?? ShortDescriptionStatus.none

  const fields = useMemo(
    (): FieldConfig[] => [
      {
        key: FIELD_KEY,
        label: 'Short Description',
        type: FieldTypeEnum.TEXTAREA,
        value: text,
        editable: true,
        rows: 3,
        maxLength: maxChars,
        patchEndpoint: PATCH_ENDPOINT
      }
    ],
    [text, maxChars]
  )

  const handleSignOff = useCallback(() => {
    void modal.confirm({
      title: 'Sign off this short description?',
      content: 'Reports print it as reviewed from now on. An edit later makes it your own text.',
      okText: 'Sign off',
      onOk: async () => {
        const failed = await signOff()
        if (failed) showErrorToast(failed)
        else toast.success('Short description signed off')
      }
    })
  }, [modal, signOff])

  const generatedOn = record?.accounts__short_description_generated_at
    ? ` on ${dayjs(record.accounts__short_description_generated_at).format(DATE_FORMAT)}`
    : ''
  const signedOffBy = `${record?.prog__signed_off_by_name ? ` by ${record.prog__signed_off_by_name}` : ''}${
    record?.accounts__short_description_signed_off_at
      ? ` on ${dayjs(record.accounts__short_description_signed_off_at).format(DATE_FORMAT)}`
      : ''
  }`

  const titleSuffix =
    status === ShortDescriptionStatus.unreviewed ? (
      <span className='flex items-center gap-1'>
        <Tooltip title={`Written by the agent${generatedOn} from this company's description and brief.`}>
          <Tag color='blue' className='!mr-0'>
            Agent-written
          </Tag>
        </Tooltip>
        <Tooltip title='Reports print it already, and warn in their preview until someone reads it and signs it off (or edits it).'>
          <Tag color='gold' className='!mr-0'>
            Not signed off
          </Tag>
        </Tooltip>
      </span>
    ) : status === ShortDescriptionStatus.signedOff ? (
      <Tooltip title={`Written by the agent${generatedOn}; signed off${signedOffBy}.`}>
        <Tag color='green' className='!mr-0'>
          Signed off
        </Tag>
      </Tooltip>
    ) : null

  const headerActions =
    status === ShortDescriptionStatus.unreviewed ? (
      <Button size='small' type='primary' loading={signingOff} onClick={handleSignOff}>
        Sign off
      </Button>
    ) : null

  return (
    <>
      {modalContext}
      <FormSection title='Short Description' titleColor='blue' titleSuffix={titleSuffix} headerActions={headerActions}>
        {!record ? (
          loading ? (
            <Spin size='small' />
          ) : (
            <Text type='danger'>{error || 'The short description could not be loaded.'}</Text>
          )
        ) : (
          <>
            <InputManager
              inputMode='read'
              labelPosition='inline'
              fields={fields}
              enableAutoPatch
              patchId={accountId}
              recordId={accountId}
              currentRecord={record}
              onPatchSuccess={() => void refetch()}
              fieldWrapping
            />
            <Text type='secondary' className='text-xs block mt-1'>
              What the company does, in one or two sentences (at most {maxChars} characters): reports print it in
              place of the Description.
              {status === ShortDescriptionStatus.none
                ? ' While it is empty, reports print the Description.'
                : ''}
            </Text>
          </>
        )}
      </FormSection>
    </>
  )
}

export default ShortDescriptionSection
